import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  FileText, BookOpen, ChevronRight, Lock, CheckCircle2,
  Sparkles, ArrowLeft, AlertCircle, RotateCcw, Trophy,
  Maximize2, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';

const PASSING_DEFAULT = 70;

/**
 * StudentManualsTab — entry point for the student. Lists manuals,
 * opens a reader with TOC + chapter-bound QCMs (blocking / non-blocking).
 */
export const StudentManualsTab = () => {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.listManuals();
        setManuals(data);
      } catch (e) { toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (active) {
    return <ManualReader manual={active} onClose={() => setActive(null)} />;
  }

  return (
    <div data-testid="student-manuals-tab">
      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">MANUELS &amp; CONTRÔLES</p>
        <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
          Tes <span className="font-serif italic text-[#C9B896]">cours & supports.</span>
        </h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl">
          Retrouve ici tous les manuels PDF de tes formations. Le sommaire est interactif et certains chapitres
          déclenchent un QCM pour valider tes acquis.
        </p>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-44 bg-white rounded-2xl animate-pulse border border-[#1F2A4A]/8" />
          ))}
        </div>
      ) : manuals.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-2xl">
          <BookOpen className="w-12 h-12 text-[#C9B896] mx-auto mb-3 opacity-50" />
          <h3 className="text-lg font-medium text-foreground">Aucun manuel disponible</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Les supports apparaîtront ici dès qu&apos;ils seront publiés par ton formateur.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {manuals.map((m) => (
            <button
              key={m.id}
              onClick={() => setActive(m)}
              className="group text-left bg-white border border-[#1F2A4A]/10 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid={`student-manual-${m.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#1F2A4A] text-white flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-[#C9B896] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-heading text-xl text-[#1F2A4A] mb-1">{m.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{m.description || 'Manuel de formation.'}</p>
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">
                <span>{m.page_count} pages</span>
                <span className="text-[#1F2A4A]/20">·</span>
                <span>{(m.chapters || []).length} chapitres</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────── Manual reader (PDF + TOC + QCM)
const ManualReader = ({ manual, onClose }) => {
  const [progress, setProgress] = useState({ by_chapter: {} });
  const [qcmsByChapter, setQcmsByChapter] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [openQcm, setOpenQcm] = useState(null);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const chapters = manual.chapters || [];

  // Fetch PDF as authenticated blob → create local object URL the iframe can load.
  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const resp = await axios.get(api.getManualFileUrl(manual.id), { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        revokeUrl = url;
        setPdfBlobUrl(url);
      } catch (e) {
        if (!cancelled) toast.error('Impossible de charger le PDF');
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [manual.id]);

  const loadAll = async () => {
    try {
      const [prog, qcms] = await Promise.all([
        api.getMyManualProgress(manual.id),
        api.listQcmsForManual(manual.id),
      ]);
      setProgress(prog || { by_chapter: {} });
      const byCh = {};
      for (const q of qcms) if (q.chapter_id) byCh[q.chapter_id] = q;
      setQcmsByChapter(byCh);
    } catch (e) {
      toast.error('Erreur de chargement du manuel');
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      try {
        const [prog, qcms] = await Promise.all([
          api.getMyManualProgress(manual.id),
          api.listQcmsForManual(manual.id),
        ]);
        if (cancelled) return;
        setProgress(prog || { by_chapter: {} });
        const byCh = {};
        for (const q of qcms) if (q.chapter_id) byCh[q.chapter_id] = q;
        setQcmsByChapter(byCh);
      } catch (e) {
        if (!cancelled) toast.error('Erreur de chargement du manuel');
      }
    })();
    return () => { cancelled = true; };
  }, [manual.id]);

  // Compute locked chapters (blocking QCM not yet passed on previous chapter)
  const isChapterLocked = (idx) => {
    if (idx === 0) return false;
    for (let i = 0; i < idx; i++) {
      const ch = chapters[i];
      const q = qcmsByChapter[ch.id];
      if (q && q.mode === 'blocking') {
        const passed = progress.by_chapter?.[ch.id]?.passed;
        if (!passed) return true;
      }
    }
    return false;
  };

  const goToChapter = (idx) => {
    if (isChapterLocked(idx)) return;
    const ch = chapters[idx];
    setActiveChapterId(ch.id);
    setCurrentPage(ch.page_start || 1);
  };

  const completedCount = chapters.filter((c) => progress.by_chapter?.[c.id]?.passed).length;
  const qcmCount = chapters.filter((c) => qcmsByChapter[c.id]).length;
  const progressPercent = qcmCount > 0 ? Math.round((completedCount / qcmCount) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#FAF7F0] overflow-y-auto"
      data-testid={`manual-reader-${manual.id}`}
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 text-sm text-[#1F2A4A]/70 hover:text-[#1F2A4A] mb-4"
          data-testid="manual-back"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux manuels
        </button>

        <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(31,42,74,0.25)]">
        {/* Header */}
        <div className="px-6 md:px-8 py-5 border-b border-[#1F2A4A]/10 bg-gradient-to-br from-[#FAF7F0] to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">MANUEL EN COURS</p>
              <h3 className="font-heading text-2xl text-[#1F2A4A]">{manual.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{manual.page_count} pages · {chapters.length} chapitres</p>
            </div>
            {qcmCount > 0 && (
              <div className="min-w-[240px]">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-[#1F2A4A]/70 font-medium">Progression QCM</span>
                  <span className="font-mono text-[#C9B896]">{completedCount}/{qcmCount}</span>
                </div>
                <Progress value={progressPercent} className="h-1.5" />
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className={`grid ${sidebarOpen ? 'lg:grid-cols-[300px_1fr]' : 'lg:grid-cols-[44px_1fr]'} transition-all`}>
          {/* TOC sidebar — collapsible */}
          <aside className="border-r border-[#1F2A4A]/8 bg-[#FAF7F0]/30 max-h-[85vh] overflow-y-auto relative" data-testid="manual-toc">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="sticky top-0 z-10 w-full bg-[#FAF7F0] border-b border-[#1F2A4A]/8 px-3 py-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.28em] text-[#1F2A4A]/70 hover:text-[#1F2A4A] hover:bg-[#C9B896]/10"
              data-testid="toc-toggle"
              title={sidebarOpen ? 'Masquer le sommaire' : 'Afficher le sommaire'}
            >
              {sidebarOpen ? <PanelLeftClose className="w-3.5 h-3.5" /> : <PanelLeftOpen className="w-3.5 h-3.5" />}
              {sidebarOpen && <span>Sommaire</span>}
            </button>
            {sidebarOpen && (
            <div className="p-5">
              {chapters.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucun chapitre détecté.</p>
              ) : (
                <ul className="space-y-1">
                  {chapters.map((ch, idx) => {
                    const qcm = qcmsByChapter[ch.id];
                    const chProg = progress.by_chapter?.[ch.id];
                    const locked = isChapterLocked(idx);
                    const isActive = activeChapterId === ch.id;
                    return (
                      <li key={ch.id}>
                        <button
                          onClick={() => goToChapter(idx)}
                          disabled={locked}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-start gap-2 ${
                            isActive ? 'bg-[#1F2A4A] text-white' :
                            locked ? 'text-[#1F2A4A]/30 cursor-not-allowed' :
                            'text-[#1F2A4A]/85 hover:bg-[#C9B896]/15'
                          }`}
                          style={{ paddingLeft: `${0.75 + (ch.level - 1) * 0.75}rem` }}
                          data-testid={`toc-chapter-${ch.id}`}
                        >
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-mono flex items-center justify-center ${
                            chProg?.passed ? 'bg-emerald-500 text-white' :
                            qcm?.mode === 'blocking' ? 'bg-red-100 text-red-700' :
                            isActive ? 'bg-[#C9B896] text-[#1F2A4A]' : 'bg-[#1F2A4A]/8 text-[#1F2A4A]'
                          }`}>
                            {chProg?.passed ? <CheckCircle2 className="w-3 h-3" /> :
                             locked ? <Lock className="w-3 h-3" /> : (idx + 1)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-tight">{ch.title}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-mono">
                              <span className={isActive ? 'text-white/60' : 'text-[#C9B896]'}>p.{ch.page_start}</span>
                              {qcm && (
                                <>
                                  <span className={isActive ? 'text-white/30' : 'text-[#1F2A4A]/30'}>·</span>
                                  <span className={
                                    qcm.mode === 'blocking'
                                      ? 'text-red-600'
                                      : (isActive ? 'text-white/70' : 'text-[#1F2A4A]/60')
                                  }>
                                    QCM {qcm.mode === 'blocking' ? 'obligatoire' : 'libre'}
                                  </span>
                                </>
                              )}
                            </div>
                            {qcm && (
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); if (!locked) setOpenQcm(qcm); }}
                                disabled={locked}
                                className={`mt-2 h-7 text-[11px] rounded-full ${
                                  chProg?.passed
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                    : 'bg-[#C9B896]/30 text-[#1F2A4A] hover:bg-[#C9B896]/60'
                                }`}
                                data-testid={`take-qcm-${qcm.id}`}
                              >
                                {chProg?.passed ? `Validé (${chProg.score_percent}%)` : 'Faire le QCM'}
                              </Button>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            )}
          </aside>

          {/* PDF viewer */}
          <div className="bg-[#1F2A4A]/5 min-h-[600px] relative">
            {pdfBlobUrl ? (
              <>
                <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                  <a
                    href={pdfBlobUrl}
                    download={`${manual.title || 'manuel'}.pdf`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white/90 hover:bg-white shadow-sm border border-[#1F2A4A]/10 rounded-full text-[#1F2A4A] backdrop-blur"
                    data-testid="manual-download"
                    title="Télécharger le PDF intact"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span>Télécharger</span>
                  </a>
                  <a
                    href={pdfBlobUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white/90 hover:bg-white shadow-sm border border-[#1F2A4A]/10 rounded-full text-[#1F2A4A] backdrop-blur"
                    data-testid="manual-fullscreen"
                    title="Ouvrir le PDF en plein écran (nouvel onglet)"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Plein écran</span>
                  </a>
                </div>
                <iframe
                  key={`${manual.id}-${currentPage}`}
                  src={`${pdfBlobUrl}#page=${currentPage}&zoom=page-width&toolbar=1&navpanes=0&view=FitH`}
                  title={manual.title}
                  className="w-full h-[88vh] border-0 bg-white"
                  data-testid="manual-pdf-iframe"
                />
              </>
            ) : (
              <div className="flex items-center justify-center h-[80vh]">
                <div className="text-center text-[#1F2A4A]/60">
                  <div className="animate-spin w-8 h-8 border-2 border-[#C9B896] border-t-transparent rounded-full mx-auto mb-3" />
                  <p className="text-sm">Chargement du manuel…</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {openQcm && (
        <QcmAttemptDialog
          qcm={openQcm}
          onClose={() => setOpenQcm(null)}
          onCompleted={async () => { await loadAll(); }}
        />
      )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────── QCM attempt UI
const QcmAttemptDialog = ({ qcm, onClose, onCompleted }) => {
  const [phase, setPhase] = useState('intro'); // intro | running | result
  const [answers, setAnswers] = useState(qcm.questions.map(() => null));
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const total = qcm.questions.length;
  const passingScore = qcm.passing_score || PASSING_DEFAULT;
  const isBlocking = qcm.mode === 'blocking';

  const allAnswered = answers.every((a) => a !== null);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.submitQcmAttempt(qcm.id, answers);
      setResult(r);
      setPhase('result');
      await onCompleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de soumission');
    } finally { setSubmitting(false); }
  };

  const retry = () => {
    setAnswers(qcm.questions.map(() => null));
    setCurrentIdx(0);
    setResult(null);
    setPhase('running');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="qcm-attempt-dialog">
        {phase === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#C9B896]" />
                {qcm.title}
              </DialogTitle>
              <DialogDescription>{qcm.intro || 'Contrôle des connaissances.'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#FAF7F0] rounded-xl p-3">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">QUESTIONS</p>
                  <p className="font-serif italic text-2xl text-[#1F2A4A] mt-1">{total}</p>
                </div>
                <div className="bg-[#FAF7F0] rounded-xl p-3">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">SEUIL</p>
                  <p className="font-serif italic text-2xl text-[#1F2A4A] mt-1">{passingScore}%</p>
                </div>
                <div className={`rounded-xl p-3 ${isBlocking ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">MODE</p>
                  <p className={`text-sm font-medium mt-2 ${isBlocking ? 'text-red-700' : 'text-emerald-700'}`}>
                    {isBlocking ? 'Bloquant' : 'Libre'}
                  </p>
                </div>
              </div>
              {isBlocking && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Tu dois obtenir au moins <strong>{passingScore}%</strong> pour débloquer le chapitre suivant.</span>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
                <Button onClick={() => setPhase('running')} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="qcm-start-btn">
                  Commencer
                </Button>
              </div>
            </div>
          </>
        )}

        {phase === 'running' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center justify-between">
                <span>{qcm.title}</span>
                <span className="text-sm font-mono text-[#C9B896]">{currentIdx + 1} / {total}</span>
              </DialogTitle>
            </DialogHeader>
            <Progress value={((currentIdx + 1) / total) * 100} className="h-1 mb-4" />
            <div className="space-y-4">
              <p className="text-[#1F2A4A] font-medium text-lg leading-relaxed" data-testid={`qcm-question-${currentIdx}`}>
                {qcm.questions[currentIdx].question}
              </p>
              <div className="space-y-2">
                {qcm.questions[currentIdx].options.map((opt, idx) => {
                  const selected = answers[currentIdx] === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        const next = [...answers];
                        next[currentIdx] = idx;
                        setAnswers(next);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        selected
                          ? 'border-[#C9B896] bg-[#C9B896]/10 shadow-sm'
                          : 'border-[#1F2A4A]/10 hover:border-[#C9B896]/40 bg-white'
                      }`}
                      data-testid={`qcm-option-${currentIdx}-${idx}`}
                    >
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono ${
                        selected ? 'bg-[#C9B896] text-[#1F2A4A]' : 'bg-[#1F2A4A]/8 text-[#1F2A4A]'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="text-sm text-[#1F2A4A] flex-1">{opt}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-[#1F2A4A]/8">
                <Button
                  variant="ghost"
                  onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                >
                  Précédent
                </Button>
                {currentIdx < total - 1 ? (
                  <Button
                    onClick={() => setCurrentIdx((i) => Math.min(total - 1, i + 1))}
                    disabled={answers[currentIdx] === null}
                    className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full"
                    data-testid="qcm-next-btn"
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={!allAnswered || submitting}
                    className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full"
                    data-testid="qcm-submit-btn"
                  >
                    {submitting ? 'Envoi…' : 'Valider mes réponses'}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {phase === 'result' && result && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                {result.passed ? (
                  <Trophy className="w-6 h-6 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                )}
                {result.passed ? 'Bravo ! QCM validé' : 'Pas encore atteint'}
              </DialogTitle>
              <DialogDescription>
                {result.passed
                  ? 'Tu maîtrises ce chapitre. Tu peux passer au suivant.'
                  : `Il te faut au moins ${passingScore}% pour valider${isBlocking ? ' et débloquer la suite' : ''}.`}
              </DialogDescription>
            </DialogHeader>
            <div className="text-center py-6">
              <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full mb-4 ${
                result.passed ? 'bg-emerald-50' : 'bg-amber-50'
              }`}>
                <span className={`font-serif italic text-5xl ${
                  result.passed ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {result.score_percent}%
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {result.correct_count} bonnes réponses sur {result.total_count}
              </p>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {qcm.questions.map((q, i) => {
                const ok = answers[i] === q.correct_index;
                return (
                  <details key={q.id} className="bg-[#FAF7F0]/60 rounded-lg p-3 text-sm">
                    <summary className="cursor-pointer flex items-center gap-2">
                      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                      <span className="font-medium">Q{i + 1}.</span> {q.question.slice(0, 60)}…
                    </summary>
                  </details>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              {!result.passed && (
                <Button variant="outline" onClick={retry} className="rounded-full" data-testid="qcm-retry-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Recommencer
                </Button>
              )}
              <Button onClick={onClose} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="qcm-close-btn">
                Fermer
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
